import React, { useMemo } from "react";
import {
  useAddOemMutation,
  useDeleteOemMutation,
  useGetOemsQuery,
  useUpdateOemMutation,
} from "../../features/oems/oemApi";
import { useLazyPlantOptions } from "../../features/plants/usePlantOptions";
import MasterCrudPage from "../masters/MasterCrudPage";
import { withAuditFilters } from "../masters/filterMetadata";
import { getPlantLabel, renderPlantCell } from "../masters/masterMetadata";

const getOemQrValue = (row) => row?.qr_value || row?.oem_id;
const getOemQrDisplayValue = (row) => row?.qr_display_text || row?.oem_id;
const oemFilterFields = withAuditFilters([
  { name: "oem_id", label: "OEM ID", optionSource: "oems" },
  { name: "oem_name", label: "OEM Name" },
  { name: "oem_code", label: "OEM Code" },
]);

const Oem = () => {
  const {
    options: plantOptions,
    isFetching: isLoadingPlants,
    loadOptions: loadPlantOptions,
  } = useLazyPlantOptions();

  const fields = useMemo(
    () => [
      {
        name: "plant_id",
        label: "Plant",
        type: "select",
        options: plantOptions,
        placeholder: isLoadingPlants ? "Loading plants..." : "Select plant",
        disabled: isLoadingPlants && plantOptions.length === 0,
        disabledOnEdit: true,
        required: true,
        fullWidth: true,
      },
      {
        name: "oem_name",
        label: "OEM Name",
        placeholder: "Enter OEM name",
        required: true,
      },
      {
        name: "oem_code",
        label: "OEM Code",
        placeholder: "Enter OEM code",
        required: true,
      },
    ],
    [isLoadingPlants, plantOptions]
  );

  const columns = useMemo(
    () => (metaData) => [
      {
        name: "OEM ID",
        selector: (row) => row?.oem_id || "-",
        sortable: true,
      },
      {
        name: "OEM Name",
        selector: (row) => row?.oem_name || "-",
        sortable: true,
        grow: 2,
      },
      {
        name: "OEM Code",
        selector: (row) => row?.oem_code || "-",
        sortable: true,
      },
      {
        name: "Plant",
        selector: (row) => getPlantLabel(row, metaData),
        cell: (row) => renderPlantCell(row, metaData),
        sortable: true,
        grow: 2,
      },
    ],
    []
  );

  return (
    <MasterCrudPage
      entityName="OEM"
      pageTitle="OEMs"
      idField="oem_id"
      displayField="oem_name"
      fields={fields}
      columns={columns}
      searchFields={[
        "oem_id",
        "oem_name",
        "oem_code",
        "plant_id",
        (row, metaData) => getPlantLabel(row, metaData),
      ]}
      searchPlaceholder="Search OEMs"
      useListQuery={useGetOemsQuery}
      useCreateMutation={useAddOemMutation}
      useUpdateMutation={useUpdateOemMutation}
      useDeleteMutation={useDeleteOemMutation}
      filterFields={oemFilterFields}
      qrDisplayValue={getOemQrDisplayValue}
      qrValue={getOemQrValue}
      showQrColumn={false}
      transformPayload={(payload, editData) => {
        if (!editData) return payload;
        const updatePayload = { ...payload };
        delete updatePayload.plant_id;
        return updatePayload;
      }}
      onOpenForm={loadPlantOptions}
      noDataLabel="No OEMs found"
    />
  );
};

export default Oem;
